import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTheme } from 'react-jss'
import {
    Root,
    Overlay,
    Content,
    Title,
    Portal,
    Close,
} from '@radix-ui/react-dialog'
import { Button } from '../../ui/Button/Button'
import { useStyles } from '../../ui/Dialog/Dialog'
import { getRejectCaseReasonList, rejectCase } from '../../../api/salary'
import { colors } from '../../../theme/palette'
import {
    IkonAfvissag,
    IkonPromptfejl,
    IkonPromptsucces,
} from '../../../assets/icons'
import { L } from '../../providers/Lokalise'
import { Select } from '../../ui/FormElememts/Select'
import { FormControl } from '../../ui/FormElememts/FormControl'
import { RejectReason } from '../../../models/RejectReason'
import { TextArea } from '../../ui/FormElememts/TextArea'
import { Typography } from '../../ui/Typography/Typography'
import { useErrorDialog } from '../../../hooks/useErrorDialog'
import { ErrorDisplay } from './Contact/ContactDialog'
import { SimpleDialog } from '../../ui/Dialog/SimpleDialog'

interface RejectCaseViewProps {
    caseId: string
    caseRegistrationDate: string
    onSuccess?: () => void
    onCancel?: () => void
    navigateToCaseList?: boolean
}

type CustomErrorsProps = {
    reason?: {
        message: string
    }
    description?: {
        message: string
    }
}

export const RejectCaseView: React.FC<RejectCaseViewProps> = ({
    caseId,
    caseRegistrationDate,
    onSuccess,
    onCancel,
    navigateToCaseList = false,
}) => {
    const theme = useTheme<Jss.Theme>()
    const navigate = useNavigate()
    const [errorMessage, setErrorMessage] = useState<React.ReactNode>('')
    const [isLoading, setIsLoading] = useState(false)
    const [rejectReason, setRejectReason] = useState<RejectReason | null>(null)
    const [rejectDescription, setRejectDescription] = useState<string>('')
    const [rejectCaseReasonList, setRejectCaseReasonList] = useState<
        RejectReason[]
    >([])
    const [caseRejected, setCaseRejected] = useState(false)
    const iconBGColor = !caseRejected ? colors.red200 : colors.blue300
    const iconBorder = colors.white
    const classes = useStyles({
        ...{ iconBGColor, iconBorder },
        theme,
    })
    const { showError, ErrorDialogComponent } = useErrorDialog() as any
    const [customErrorMessage, setCustomErrorMessage] = useState<
        CustomErrorsProps | undefined
    >(undefined)
    const [openErrorDialog, setOpenErrorDialog] = useState<boolean>(false)

    useEffect(() => {
        const fetchRejectCaseReasonList = async (): Promise<void> => {
            const response = await getRejectCaseReasonList()
            if (response.error) {
                setErrorMessage(<ErrorDisplay errors={response.error.errors} />)
                setIsLoading(false)
                onCancel()
                return
            }
            const mapped = response.data.map((item) => RejectReason.build(item))
            setRejectCaseReasonList(mapped)
        }

        fetchRejectCaseReasonList()
    }, [])

    const handleRejectCase = async (): Promise<void> => {
        const customError: CustomErrorsProps = {}
        if (!rejectReason) {
            customError.reason = {
                message: 'Årsag er påkrævet',
            }
        }
        if (
            rejectReason &&
            rejectReason.requiresFreeText &&
            !rejectDescription
        ) {
            customError.description = {
                message: 'Beskrivelse er påkrævet',
            }
        }

        if (Object.keys(customError).length > 0) {
            setCustomErrorMessage(customError)
            return
        }

        setIsLoading(true)

        try {
            const response = await rejectCase(
                caseId,
                caseRegistrationDate,
                String(rejectReason?.id),
                String(rejectDescription)
            )

            if (response.error) {
                setErrorMessage(<ErrorDisplay errors={response.error.errors} />)
                setOpenErrorDialog(true)
                setIsLoading(false)
                return
            }
            setIsLoading(false)
        } catch (error) {
            // console.error('Der skete en fejl - kontakt support', error)
            setErrorMessage(
                <ErrorDisplay
                    errors={{
                        'Der skete en fejl - kontakt support': [
                            'Der skete en fejl - kontakt support',
                        ],
                    }}
                />
            )
            setOpenErrorDialog(true)
            setIsLoading(false)
            return
        }

        setCaseRejected(true)
    }

    const renderRejectCaseStep = (): JSX.Element => {
        return (
            <>
                <div className={classes.circleIcon}>
                    <IkonAfvissag
                        width="46px"
                        fill={
                            !caseRejected ? colors.caseRed500 : colors.green500
                        }
                    />
                </div>
                {/* <Title style={{ marginBottom: '30px' }}>Afvis sagen</Title> */}
                <Typography tag="h2" size="L" style={{ marginBottom: '30px' }}>
                    {/* Afvis sagen */}
                    {L('LØN_AFVIS_SAG_DIALOG_AFVIS_SAG_TITEL')}
                </Typography>
                <div
                    style={{
                        width: '100%',
                        textAlign: 'left',
                    }}
                >
                    <FormControl>
                        <Select
                            options={rejectCaseReasonList?.map((item) => ({
                                label: item.text,
                                value: String(item.id),
                            }))}
                            required
                            onChange={(value) => {
                                setCustomErrorMessage(undefined)
                                const r = rejectCaseReasonList.find(
                                    (item) =>
                                        item.id === Number(value.target.value)
                                )

                                if (r) {
                                    setRejectReason(r)
                                }
                            }}
                            placeholder={`${L(
                                'LØN_AFVIS_SAG_DIALOG_VÆLG_ÅRSAG_PLACEHOLDER'
                            )} *`}
                            value={rejectReason?.id}
                            hasError={!!customErrorMessage?.reason}
                        />
                    </FormControl>
                    <FormControl>
                        <TextArea
                            label={
                                rejectReason?.requiresFreeText === true
                                    ? L(
                                          'LØN_AFVIS_SAG_DIALOG_VÆLG_KOMMENTAR_REQUIRED_PLACEHOLDER'
                                      )
                                    : L(
                                          'LØN_AFVIS_SAG_DIALOG_VÆLG_KOMMENTAR_OPTIONAL_PLACEHOLDER'
                                      )
                            }
                            onChange={(e) => {
                                setRejectDescription(e.target.value)
                            }}
                            required={rejectReason?.requiresFreeText}
                            rows={1}
                            maxCount={100}
                            hasError={!!customErrorMessage?.description}
                        />
                    </FormControl>
                    <FormControl>
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-evenly',
                            }}
                        >
                            <Button
                                size="xlarge"
                                type="secondary"
                                style={{
                                    flex: 1,
                                }}
                                onClick={() => {
                                    onCancel?.()
                                }}
                            >
                                {L('LØN_AFVIS_SAG_DIALOG_FORTYD_KNAP_LABEL')}
                            </Button>
                            <Button
                                size="xlarge"
                                type="danger"
                                disabled={isLoading}
                                loading={isLoading}
                                style={{
                                    flex: 1,
                                }}
                                onClick={handleRejectCase}
                            >
                                {L('LØN_AFVIS_SAG_DIALOG_AFVIS_KNAP_LABEL')}
                            </Button>
                        </div>
                    </FormControl>
                </div>
                {openErrorDialog && (
                    <SimpleDialog
                        title="Fejl"
                        message={errorMessage}
                        closeDialog={() => setOpenErrorDialog(false)}
                        isError
                        icon={
                            <IkonPromptfejl width="46px" fill={colors.red500} />
                        }
                    />
                )}
            </>
        )
    }

    const renderCaseRejectedStep = (): JSX.Element => {
        return (
            <>
                <div className={classes.circleIcon}>
                    <IkonPromptsucces width="46px" fill={colors.green} />
                </div>
                <Typography tag="h2" size="L" style={{ marginBottom: '30px' }}>
                    {/* Sagen er afvist */}
                    {L('LØN_AFVIS_SAG_DIALOG_AFVIS_SAG_TITEL_2')}
                </Typography>
                <Typography tag="p" size="S" style={{ marginBottom: '30px' }}>
                    {/* Sagen er sendt tilbage til din fagforening. */}
                    {L('LØN_AFVIS_SAG_DIALOG_SAG_AFVIST_BESKRIVELSE')}
                </Typography>
                <Close asChild>
                    <Button
                        size="xlarge"
                        type="primary"
                        loading={isLoading}
                        disabled={isLoading}
                        onClick={() => {
                            if (navigateToCaseList) {
                                navigate('/mine-lonindplaceringer')
                                return
                            }
                            onSuccess?.()
                        }}
                    >
                        {L('LØN_AFVIS_SAG_DIALOG_OK_KNAP_LABEL')}
                    </Button>
                </Close>
            </>
        )
    }

    return (
        <div className={`${classes.container} withIcon`}>
            {!caseRejected ? renderRejectCaseStep() : renderCaseRejectedStep()}
            <ErrorDialogComponent />
        </div>
    )
}

interface RejectCaseProps {
    caseId: string
    caseRegistrationDate: string
    onSuccess?: () => void
    handleClose?: () => void
    open?: boolean
    navigateToCaseList?: boolean
}

export const RejectCase: React.FC<RejectCaseProps> = ({
    caseId,
    caseRegistrationDate,
    onSuccess,
    open,
    handleClose,
    navigateToCaseList = false,
}) => {
    const theme = useTheme<Jss.Theme>()
    const iconBGColor = colors.blue300
    const iconBorder = colors.white

    const classes = useStyles({
        ...{ iconBGColor, iconBorder },
        theme,
    })

    return (
        <Root defaultOpen>
            <Portal>
                <Overlay className={classes.overlay} />
                <Content className={`${classes.overlayContainer} withIcon`}>
                    <RejectCaseView
                        caseId={caseId}
                        caseRegistrationDate={caseRegistrationDate}
                        onSuccess={onSuccess}
                        onCancel={handleClose}
                        navigateToCaseList={navigateToCaseList}
                    />
                </Content>
            </Portal>
        </Root>
    )
}
